import {
  getCookieConsent,
  getStoredCookieConsent,
  hasAdsConsent,
  hasAnalyticsConsent,
  type CookieConsentValue,
} from "./consent";
import {
  GOOGLE_CONSENT_DENIED,
  googleConsentSettingsFor,
  pushGoogleAdsRestrictions,
  pushGoogleConsentCommand,
} from "./consent-mode";
import {
  buildCanonicalMarketingPagePath,
  isMarketingAnalyticsHostAllowed,
} from "./host-guard";
import { marketingAnalyticsEnv } from "./env";
import {
  ensureOpenAiAdsPixel,
  isOpenAiAdsPixelLoaded,
} from "./openai-ads-pixel";
import {
  MARKETING_SESSION_RECORDING_DELAY_MS,
  MARKETING_SESSION_RECORDING_OPTIONS,
} from "./posthog-session-recording";
import { scheduleIdle } from "./schedule-idle";
import {
  buildPosthogPageViewProperties,
  POSTHOG_PAGEVIEW_EVENT,
  RIVETAI_ANALYTICS_SURFACE,
} from "./surface";

let consentDefaultApplied = false;
let posthogInitialized = false;
let initPromise: Promise<void> | null = null;
let sessionRecordingScheduled = false;
let posthogModule: typeof import("posthog-js").default | null = null;
let sessionRecordingTimeoutId: ReturnType<typeof setTimeout> | null = null;
/** Bumped on every withdrawal, so a start-up still loading the library gives up. */
let consentWithdrawals = 0;

const GTM_SCRIPT_ID = "rivetai-gtm-script";

const isGtmLoaded = (): boolean =>
  typeof document !== "undefined" &&
  document.getElementById(GTM_SCRIPT_ID) !== null;

const injectGtm = (gtmId: string): void => {
  if (typeof window === "undefined") return;
  if (isGtmLoaded()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);
};

const applyConsentDefaultIfNeeded = (): void => {
  if (consentDefaultApplied) return;

  pushGoogleConsentCommand("default", {
    ...GOOGLE_CONSENT_DENIED,
    wait_for_update: 500,
  });

  const consent = getCookieConsent();
  if (consent && consent !== "essential") {
    pushGoogleConsentCommand("update", googleConsentSettingsFor(consent));
  }
  // Before gtm.js so page-load tags already see the restriction.
  if (!hasAdsConsent()) {
    pushGoogleAdsRestrictions();
  }

  consentDefaultApplied = true;
};

/**
 * Load GTM only after analytics consent, with Consent Mode v2 defaults denied
 * until the visitor's choice is applied. Nothing reaches Google before consent.
 */
export const ensureGoogleTagManager = (): void => {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;

  const { gtmId } = marketingAnalyticsEnv;
  if (!gtmId) return;

  // Tests / remounts may tear down the script node; allow re-applying defaults.
  if (!isGtmLoaded()) {
    consentDefaultApplied = false;
  }

  applyConsentDefaultIfNeeded();
  injectGtm(gtmId);
};

export const syncGoogleConsentMode = (
  consent: CookieConsentValue | null,
): void => {
  if (typeof window === "undefined") return;
  if (!marketingAnalyticsEnv.gtmId) return;

  ensureGoogleTagManager();
  // Without consent GTM never loads, so there is nothing to update.
  if (!isGtmLoaded()) return;
  pushGoogleConsentCommand("update", googleConsentSettingsFor(consent));
};

/** GTM or the OpenAI Ads pixel is on the page. Their tags keep running until the page reloads. */
export const hasConsentedTagsOnPage = (): boolean =>
  isGtmLoaded() || isOpenAiAdsPixelLoaded();

/** Reload so tags loaded under a withdrawn consent are gone from the page. */
export const reloadPage = (): void => {
  window.location.reload();
};

/**
 * Session replay needs an explicit "Accept all" click. A regional default
 * (US visitors before any choice) never starts it.
 */
const hasSessionRecordingConsent = (): boolean =>
  getStoredCookieConsent() === "all";

const clearSessionRecordingSchedule = (): void => {
  if (sessionRecordingTimeoutId !== null) {
    clearTimeout(sessionRecordingTimeoutId);
    sessionRecordingTimeoutId = null;
  }
  sessionRecordingScheduled = false;
};

const scheduleSessionRecording = (
  posthog: typeof import("posthog-js").default,
): void => {
  if (!hasSessionRecordingConsent() || sessionRecordingScheduled) return;
  sessionRecordingScheduled = true;

  if (MARKETING_SESSION_RECORDING_DELAY_MS === 0) {
    posthog.startSessionRecording();
    return;
  }

  sessionRecordingTimeoutId = setTimeout(() => {
    sessionRecordingTimeoutId = null;
    if (!hasSessionRecordingConsent() || !posthogInitialized) return;
    posthog.startSessionRecording();
  }, MARKETING_SESSION_RECORDING_DELAY_MS);
};

const loadPosthogModule = async (): Promise<
  typeof import("posthog-js").default | null
> => {
  if (!marketingAnalyticsEnv.posthogKey) return null;
  if (!posthogModule) {
    posthogModule = (await import("posthog-js")).default;
  }
  return posthogModule;
};

const initPosthog = async (): Promise<void> => {
  if (posthogInitialized) return;

  const withdrawalsAtStart = consentWithdrawals;
  const posthog = await loadPosthogModule();
  if (!posthog) return;
  // Consent was withdrawn while the library loaded: never opt in.
  if (withdrawalsAtStart !== consentWithdrawals || !hasAnalyticsConsent())
    return;

  posthog.init(marketingAnalyticsEnv.posthogKey, {
    api_host: marketingAnalyticsEnv.posthogHost,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    // Multivariate website experiments rely on flag bootstrap with init.
    advanced_disable_feature_flags: false,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    disable_session_recording: true,
    session_recording: MARKETING_SESSION_RECORDING_OPTIONS,
    persistence: "localStorage+cookie",
  });
  posthog.register({
    analytics_schema_version: 2,
    rivetai_surface: RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
  });
  posthog.opt_in_capturing();
  scheduleSessionRecording(posthog);
  posthogInitialized = true;
};

/** Synced PostHog client after init; null when not ready or not consented. */
export const getPosthogClient = ():
  | typeof import("posthog-js").default
  | null => {
  if (!posthogInitialized || !posthogModule) {
    return null;
  }
  return posthogModule;
};

export const isPosthogReady = (): boolean => posthogInitialized;

/** Initialize consented analytics (GTM, PostHog, and the OpenAI Ads pixel). */
export const initializeMarketingAnalytics = (): Promise<void> => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) {
    return Promise.resolve();
  }

  syncGoogleConsentMode(getCookieConsent());
  // An ad pixel: skipped when ads are denied (GPC or "Do not sell or share").
  if (hasAdsConsent()) {
    ensureOpenAiAdsPixel();
  }

  if (initPromise) {
    // A US visitor who later clicks "Accept all" starts session replay here.
    void initPromise.then(() => {
      if (posthogInitialized && posthogModule) {
        scheduleSessionRecording(posthogModule);
      }
    });
    return initPromise;
  }

  initPromise = new Promise((resolve) => {
    scheduleIdle(() => {
      void (async () => {
        await initPosthog();
        resolve();
      })();
    });
  });

  return initPromise;
};

/** PostHog persists its identity as `ph_<key>_posthog` in localStorage and a cookie. */
const hasStoredPosthogState = (): boolean => {
  const { posthogKey } = marketingAnalyticsEnv;
  if (!posthogKey) return false;
  const name = `ph_${posthogKey}_posthog`;
  try {
    if (window.localStorage.getItem(name) !== null) return true;
  } catch {
    // Storage can be blocked; the cookie check below still runs.
  }
  return document.cookie
    .split(";")
    .some((cookie) => cookie.trim().startsWith(`${name}=`));
};

/**
 * Revoke consented analytics: PostHog opts out and a loaded GTM gets Consent Mode denied.
 * Call reloadPage afterwards when hasConsentedTagsOnPage was true, to remove those tags.
 */
export async function disableMarketingAnalytics(): Promise<void> {
  if (typeof window === "undefined") return;

  consentWithdrawals += 1;
  clearSessionRecordingSchedule();
  syncGoogleConsentMode("essential");
  initPromise = null;

  // Without consent this runs on every page load. A visitor PostHog never
  // touched has nothing to clear, so skip downloading the library.
  if (!posthogModule && !hasStoredPosthogState()) {
    posthogInitialized = false;
    return;
  }

  const posthog = await loadPosthogModule();
  if (posthog && marketingAnalyticsEnv.posthogKey) {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    posthog.reset();
  }

  posthogInitialized = false;
}

export const captureMarketingException = async (
  error: Error,
  properties?: Record<string, string | number | boolean>,
): Promise<void> => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  if (!isMarketingAnalyticsHostAllowed()) return;

  await initializeMarketingAnalytics();

  const posthog = await loadPosthogModule();
  if (!posthog || !marketingAnalyticsEnv.posthogKey) return;

  posthog.captureException(error, properties);
};

export const captureMarketingEvent = async (
  event: string,
  properties?: Record<string, string | number | boolean>,
): Promise<void> => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  if (!isMarketingAnalyticsHostAllowed()) return;

  await initializeMarketingAnalytics();

  const { gtmId, posthogKey } = marketingAnalyticsEnv;

  if (gtmId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      analytics_schema_version: 2,
      rivetai_surface: RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
      ...properties,
    });
  }

  const posthog = await loadPosthogModule();
  if (posthog && posthogKey) {
    const anonymousId = posthog.get_property("$device_id");
    posthog.capture(event, {
      analytics_schema_version: 2,
      rivetai_surface: RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
      ...(typeof anonymousId === "string" && anonymousId
        ? { anonymous_id: anonymousId }
        : {}),
      ...properties,
    });
  }
};

export type CaptureMarketingPageViewInput = {
  pathname: string;
  search?: string;
  pageTitle: string;
  attribution?: Record<string, string>;
};

export const captureMarketingPageView = async ({
  pathname,
  search = "",
  pageTitle,
  attribution = {},
}: CaptureMarketingPageViewInput): Promise<void> => {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  if (!isMarketingAnalyticsHostAllowed()) return;

  await initializeMarketingAnalytics();

  const canonicalPagePath = buildCanonicalMarketingPagePath(pathname);
  const marketingProperties = {
    source: RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
    page_path: canonicalPagePath,
    page_title: pageTitle,
    ...attribution,
  };

  const { gtmId, posthogKey } = marketingAnalyticsEnv;

  if (gtmId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "marketing_page_viewed",
      ...marketingProperties,
    });
  }

  const posthog = await loadPosthogModule();
  if (!posthog || !posthogKey) return;

  posthog.capture(POSTHOG_PAGEVIEW_EVENT, {
    analytics_schema_version: 2,
    anonymous_id: String(posthog.get_property("$device_id") ?? ""),
    ...buildPosthogPageViewProperties(
      RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
      pathname,
      search,
    ),
    source: RIVETAI_ANALYTICS_SURFACE.marketingWebsite,
    page_path: canonicalPagePath,
    page_title: pageTitle,
    ...attribution,
  });
};
